(function () {
  const MOBILE_MQ = window.matchMedia('(max-width: 799px)');
  const STORAGE_PREFIX = 'charm-builder:';

  function stripTrailingMoneyZeros(formatted) {
    if (!formatted) return '';
    return formatted.replace(/([.,]00)(?=\D|$)/g, '');
  }

  function formatMoney(cents, moneyFormat) {
    if (typeof cents !== 'number') return '';
    let formatted;
    if (window.Shopify?.formatMoney) {
      formatted = window.Shopify.formatMoney(
        cents,
        moneyFormat || window.themeVariables?.settings?.moneyFormat
      );
    } else {
      formatted = `$${(cents / 100).toFixed(2).replace(/\.00$/, '')}`;
    }
    return stripTrailingMoneyZeros(formatted);
  }

  function generateLineId() {
    return `cb-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;
  }

  class CharmBundleStore extends EventTarget {
    constructor(sectionId, pageHandle) {
      super();
      this.sectionId = sectionId;
      this.pageHandle = pageHandle || '';
      this.lines = [];
      this._storageKey = `${STORAGE_PREFIX}${sectionId}`;
    }

    getState() {
      const productIds = new Set(this.lines.map((l) => l.productId));
      return {
        lines: [...this.lines],
        total: this.lines.reduce((sum, l) => sum + (l.price || 0), 0),
        count: this.lines.length,
        selectedProductIds: productIds,
      };
    }

    _emit(type, payload) {
      this.dispatchEvent(
        new CustomEvent('charm-builder:change', {
          detail: { type, payload, state: this.getState() },
        })
      );
    }

    _persist() {
      try {
        sessionStorage.setItem(this._storageKey, JSON.stringify({ lines: this.lines }));
      } catch (e) {
        /* ignore quota */
      }
    }

    load() {
      try {
        const raw = sessionStorage.getItem(this._storageKey);
        if (!raw) return;
        const data = JSON.parse(raw);
        if (Array.isArray(data.lines)) {
          this.lines = data.lines;
          this._emit('hydrate', {});
        }
      } catch (e) {
        /* ignore */
      }
    }

    clear() {
      this.lines = [];
      try {
        sessionStorage.removeItem(this._storageKey);
      } catch (e) {
        /* ignore */
      }
      this._emit('clear', {});
    }

    getLinesForProduct(blockId, productId) {
      const pid = Number(productId);
      return this.lines.filter(
        (l) => l.blockId === blockId && Number(l.productId) === pid
      );
    }

    hasProduct(blockId, productId) {
      return this.getLinesForProduct(blockId, productId).length > 0;
    }

    upsertSingleProduct(line) {
      this.lines = this.lines.filter(
        (l) => !(l.blockId === line.blockId && Number(l.productId) === Number(line.productId))
      );
      this.lines.push({
        ...line,
        productId: Number(line.productId),
        lineId: line.lineId || generateLineId(),
        quantity: 1,
      });
      this._persist();
      this._emit('upsert', { line });
    }

    /** Replace all charm line units for one product (aggregated by variant qty). */
    setCharmUnits(blockId, productId, entries) {
      const pid = Number(productId);
      this.lines = this.lines.filter(
        (l) => !(l.blockId === blockId && Number(l.productId) === pid)
      );

      (entries || []).forEach((entry) => {
        const qty = entry.quantity || 0;
        for (let i = 0; i < qty; i += 1) {
          this.lines.push({
            lineId: generateLineId(),
            blockId: entry.blockId,
            blockType: 'charms',
            productId: pid,
            variantId: entry.variantId,
            quantity: 1,
            price: entry.price,
            title: entry.title,
            variantTitle: entry.variantTitle,
            image: entry.image,
          });
        }
      });

      this._persist();
      this._emit('sync-charms', { blockId, productId: pid, entries: entries || [] });
    }

    addCharmUnits(entries) {
      entries.forEach((entry) => {
        for (let i = 0; i < entry.quantity; i += 1) {
          this.lines.push({
            lineId: generateLineId(),
            blockId: entry.blockId,
            blockType: 'charms',
            productId: Number(entry.productId),
            variantId: entry.variantId,
            quantity: 1,
            price: entry.price,
            title: entry.title,
            variantTitle: entry.variantTitle,
            image: entry.image,
          });
        }
      });
      this._persist();
      this._emit('add-charms', { entries });
    }

    removeLine(lineId) {
      const before = this.lines.length;
      this.lines = this.lines.filter((l) => l.lineId !== lineId);
      if (this.lines.length !== before) {
        this._persist();
        this._emit('remove', { lineId });
      }
    }

  }

  function getOptionValue(variant, position) {
    if (position === 1) return variant.option1;
    if (position === 2) return variant.option2;
    if (position === 3) return variant.option3;
    return null;
  }

  function getChainBlockSettings(block) {
    if (block?.type !== 'chain') return null;
    return {
      excludeLengthContains: (block.excludeLengthContains || '').trim(),
      removeFromDisplay: (block.removeFromLengthDisplay || '').trim(),
    };
  }

  function applyChainLengthPoolFilter(variants, product, chainSettings) {
    if (!chainSettings?.excludeLengthContains) return variants;
    return variants.filter((v) => !isChainLengthExcluded(v, product, chainSettings));
  }

  function getChainLengthValue(variant, product) {
    const picker = product?.pickerOption;
    if (!picker) return variant?.title || '';
    return getOptionValue(variant, picker.position) || variant?.title || '';
  }

  function isChainLengthExcluded(variant, product, chainSettings) {
    const needle = chainSettings?.excludeLengthContains;
    if (!needle) return false;
    const haystack = getChainLengthValue(variant, product);
    return haystack.toLowerCase().includes(needle.toLowerCase());
  }

  function formatChainLengthDisplay(value, chainSettings) {
    if (!value) return value;
    const removeText = chainSettings?.removeFromDisplay;
    if (!removeText) return value;
    const escaped = removeText.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    return value.replace(new RegExp(escaped, 'gi'), '').replace(/\s+/g, ' ').trim();
  }

  function getChainAvailableVariants(product, variantDefault, chainSettings) {
    const vd = (variantDefault || product?.variantDefault || '').toLowerCase().trim();
    return (product?.variants || []).filter((v) => {
      if (isChainLengthExcluded(v, product, chainSettings)) return false;
      return variantMatchesDefault(v, vd);
    });
  }

  function resolveChainGalleryVariant(product, variant, block, variantDefault) {
    const chainSettings = getChainBlockSettings(block);
    const available = getChainAvailableVariants(product, variantDefault, chainSettings);
    if (available.length !== 1) return variant;
    const sole = available[0];
    const img = getModalPrimaryImageSrc(sole, product, false);
    return img ? sole : variant;
  }

  /** Same variant the chain modal uses when only one option is shown. */
  function resolveChainCardVariant(product, block) {
    if (block?.type !== 'chain' || !product) return null;
    const chainSettings = getChainBlockSettings(block);
    const vd = (block.variantDefault || product.variantDefault || '').toLowerCase().trim();
    const available = getChainAvailableVariants(product, vd, chainSettings);
    if (available.length === 1) return available[0];
    if (!variantNeedsSelection(product, vd, chainSettings)) {
      return getDefaultChainVariant(product, vd, chainSettings);
    }
    return null;
  }

  /** First image shown in the chain/connector modal gallery. */
  function getModalPrimaryImageSrc(variant, product, preferByoc = false) {
    const gallery = getVariantGalleryImages(variant, product, preferByoc);
    return gallery[0] || '';
  }

  function normalizeProductId(id) {
    return Number(id);
  }

  function normalizeVariantId(id) {
    return Number(id);
  }

  function normalizeVariantTitle(title) {
    return (title || '').toLowerCase().replace(/\s*\/\s*/g, ' ').replace(/\s+/g, ' ').trim();
  }

  function normalizeVariantSearchText(variant) {
    const chunks = [
      variant?.title,
      variant?.option1,
      variant?.option2,
      variant?.option3,
    ]
      .filter(Boolean)
      .join(' ');
    return normalizeVariantTitle(chunks)
      .replace(/\bsolid\b/g, '')
      .replace(/\bgold\b/g, '')
      .replace(/\s+/g, ' ')
      .trim();
  }

  function variantMatchesDefault(variant, variantDefault) {
    if (!variantDefault) return true;
    const haystack = normalizeVariantSearchText(variant);
    const needle = variantDefault
      .toLowerCase()
      .trim()
      .replace(/\bgold\b/g, '')
      .replace(/\s+/g, ' ')
      .trim();
    if (needle && haystack.includes(needle)) return true;
    const parts = needle.split(/\s+/).filter(Boolean);
    return parts.length ? parts.every((part) => haystack.includes(part)) : true;
  }

  function filterVariantsByDefault(variants, variantDefault) {
    const vd = (variantDefault || '').toLowerCase().trim();
    if (!vd) return variants;
    return variants.filter((v) => variantMatchesDefault(v, vd));
  }

  function getPickerValues(product, _variantDefault, chainSettings) {
    const picker = product.pickerOption;
    if (!picker) return [];
    const values = new Set();
    product.variants.forEach((v) => {
      if (isChainLengthExcluded(v, product, chainSettings)) return;
      const val = getOptionValue(v, picker.position);
      if (val) values.add(val);
    });
    return Array.from(values).sort((a, b) => String(a).localeCompare(String(b), undefined, { numeric: true }));
  }

  function findVariant(product, selectedOptions, variantDefault, chainSettings) {
    if (!product?.variants?.length) return null;
    const vd = (variantDefault || '').toLowerCase().trim();

    if (product.hasOnlyDefaultVariant || !product.pickerOption) {
      let pool = filterVariantsByDefault(product.variants, vd);
      pool = applyChainLengthPoolFilter(pool, product, chainSettings);
      if (!pool.length) return null;
      return pool.find((v) => v.available) || pool[0];
    }

    const picker = product.pickerOption;
    const key = `option${picker.position}`;
    const val = selectedOptions[key];
    if (!val) return null;

    let matches = product.variants.filter((v) => getOptionValue(v, picker.position) === val);
    matches = applyChainLengthPoolFilter(matches, product, chainSettings);
    let pool = filterVariantsByDefault(matches, vd);
    pool = applyChainLengthPoolFilter(pool, product, chainSettings);
    if (!pool.length) return null;
    return pool.find((v) => v.available) || pool[0] || null;
  }

  function getDefaultChainVariant(product, variantDefault, chainSettings) {
    if (!product?.variants?.length) return null;

    const picker = product.pickerOption;
    if (picker) {
      const values = getPickerValues(product, variantDefault, chainSettings);
      if (values.length >= 1) {
        const key = `option${picker.position}`;
        const selectedOptions = { option1: null, option2: null, option3: null };
        selectedOptions[key] = values[0];
        const match = findVariant(product, selectedOptions, variantDefault, chainSettings);
        if (match) return match;
      }
    }

    const available = getChainAvailableVariants(product, variantDefault, chainSettings);
    return available.find((v) => v.available) || available[0] || null;
  }

  function variantNeedsSelection(product, variantDefault, chainSettings) {
    return Boolean(
      product.pickerOption &&
        getPickerValues(product, variantDefault, chainSettings).length > 1
    );
  }

  function isMetalGoldColor(option1) {
    const v = (option1 || '').toLowerCase().trim();
    if (!v || v.includes(',')) return false;
    const pureMetals = new Set([
      'white gold',
      'yellow gold',
      'rose gold',
      '14k white gold',
      '14k yellow gold',
      '14k rose gold',
    ]);
    return pureMetals.has(v);
  }

  function usesCharmColorPicker(product) {
    if (product?.charmPickerMode === 'color') return true;
    return getCharmColorValues(product).length > 1;
  }

  function getCharmColorValues(product) {
    const values = new Set();
    (product?.variants || []).forEach((v) => {
      if (v.option1 && !isMetalGoldColor(v.option1)) values.add(v.option1);
    });
    return Array.from(values);
  }

  function findCharmVariantByColor(product, colorValue, variantDefault) {
    if (!product?.variants?.length || !colorValue) return null;
    const matches = product.variants.filter((v) => v.option1 === colorValue);
    const pool = filterVariantsByDefault(matches, variantDefault);
    if (!pool.length) return null;
    return pool.find((v) => v.available) || pool[0] || null;
  }

  function getCharmLetterValues(product) {
    if (usesCharmColorPicker(product)) return [];
    const position = product?.pickerOption?.position;
    const values = new Set();
    (product?.variants || []).forEach((v) => {
      const val = getOptionValue(v, position);
      if (val) values.add(val);
    });
    return Array.from(values).sort((a, b) =>
      String(a).localeCompare(String(b), undefined, { numeric: true })
    );
  }

  function findCharmVariantByOption3(product, letter, variantDefault) {
    if (!product?.variants?.length || !letter) return null;
    const position = product?.pickerOption?.position;
    const matches = product.variants.filter((v) => getOptionValue(v, position) === letter);
    const pool = filterVariantsByDefault(matches, variantDefault);
    if (!pool.length) return null;
    return pool.find((v) => v.available) || pool[0] || null;
  }

  function charmLetterSelectionRequired(product) {
    return getCharmLetterValues(product).length > 1;
  }

  function charmShowsVariantPicker(product) {
    if (usesCharmColorPicker(product)) return true;
    return charmLetterSelectionRequired(product);
  }

  /** featured_media → byoc_img → product featured (variants-card-byoc.liquid) */
  function getVariantGalleryImages(variant, product, preferByoc) {
    const images = [];
    const add = (url) => {
      if (url && !images.includes(url)) images.push(url);
    };

    const productFeatured = product?.featuredImage || '';
    const variantImg = variant?.image || '';
    const variantImgIsProductFeatured =
      Boolean(variantImg && productFeatured && variantImg === productFeatured);

    if (preferByoc && variant?.byocImage) add(variant.byocImage);
    if (variant?.byocImage) add(variant.byocImage);
    if (variantImg && !variantImgIsProductFeatured) add(variantImg);
    (variant?.media || []).forEach(add);
    if (variantImgIsProductFeatured) add(variantImg);
    if (variantImg && !images.includes(variantImg)) add(variantImg);
    if (productFeatured) add(productFeatured);
    if (!images.length && product?.variants?.[0]?.media) {
      product.variants[0].media.forEach(add);
    }

    return images.slice(0, 3);
  }

  function getCharmVariantImage(variant, preferByoc, product) {
    if (!variant && product?.featuredImage) return product.featuredImage;
    const images = getVariantGalleryImages(variant, product, preferByoc);
    return images[0] || '';
  }

  function applyVariantToDraftOptions(draft, variant) {
    if (!draft || !variant) return;
    draft.selectedOptions.option1 = variant.option1;
    draft.selectedOptions.option2 = variant.option2;
    draft.selectedOptions.option3 = variant.option3;
  }

  function getDefaultCharmVariant(product, variantDefault) {
    if (!product?.variants?.length) return null;

    if (usesCharmColorPicker(product)) {
      const colors = getCharmColorValues(product);
      const productDefault = variantDefault;
      const firstColor =
        colors.find((c) => findCharmVariantByColor(product, c, productDefault)?.available) ||
        colors[0];
      const colorVariant = findCharmVariantByColor(product, firstColor, productDefault);
      if (colorVariant) return colorVariant;
      return product.variants.find((v) => v.available) || product.variants[0] || null;
    }

    const letters = getCharmLetterValues(product);
    if (letters.length) {
      const firstLetter =
        letters.find((l) => findCharmVariantByOption3(product, l, variantDefault)?.available) ||
        letters[0];
      const letterVariant = findCharmVariantByOption3(product, firstLetter, variantDefault);
      if (letterVariant) return letterVariant;
      return product.variants.find((v) => v.available) || product.variants[0] || null;
    }

    if (!charmShowsVariantPicker(product)) {
      return product.variants.find((v) => v.available) || product.variants[0] || null;
    }

    return findVariant(
      product,
      { option1: null, option2: null, option3: null },
      variantDefault
    );
  }

  class CharmBuilderModal {
    constructor(root, store, data) {
      this.root = root;
      this.store = store;
      this.data = data;
      this.sectionId = data.sectionId;
      this._dialog = root.querySelector('[data-charm-builder-modal]');
      this._track = root.querySelector('[data-modal-track]');
      this._titleEl = root.querySelector('[data-modal-block-title]');
      this._prevBtn = root.querySelector('[data-modal-prev]');
      this._nextBtn = root.querySelector('[data-modal-next]');
      this._dotsEl = root.querySelector('[data-modal-slide-dots]');
      this._counterEl = root.querySelector('[data-modal-counter]');
      this._draft = null;
      this._block = null;
      this._products = [];
      this._slideIndex = 0;
      this._slides = new Map();
      this._isCharmsMode = false;
      this._variantDefault = '';

      this._viewport = root.querySelector('[data-modal-viewport]');
      this._bindChrome();
      this._bindMobileSwipe();
      this._onStoreChange = this._handleStoreChange.bind(this);
      this.store.addEventListener('charm-builder:change', this._onStoreChange);
    }

    _handleStoreChange() {
      if (!this._dialog?.open || !this._isCharmsMode || !this._draft) return;
      const pid = normalizeProductId(this._draft.productId);
      this._draft.quantities = this._getCharmQuantitiesFromStore(pid);
      this._syncCharmSlideUI(pid);
    }

    _resolveVariantDefault(block) {
      return (block?.variantDefault || '').toLowerCase().trim();
    }

    _findCharmProduct(block, productId) {
      const id = Number(productId);
      for (const col of block.collections || []) {
        const product = col.products?.find((p) => Number(p.id) === id);
        if (product) return product;
      }
      return null;
    }

    _getCharmVariantDefault(product) {
      return (product?.variantDefault || this._variantDefault || '').toLowerCase().trim();
    }

    _bindChrome() {
      this.root.querySelectorAll('[data-modal-close]').forEach((el) => {
        el.addEventListener('click', () => this.close());
      });
      this._dialog?.addEventListener('cancel', (e) => {
        e.preventDefault();
        this.close();
      });
      this._prevBtn?.addEventListener('click', () => this._goSlide(-1));
      this._nextBtn?.addEventListener('click', () => this._goSlide(1));
    }

    _bindMobileSwipe() {
      if (!this._viewport || this._mobileSwipeBound) return;
      this._mobileSwipeBound = true;

      const THRESHOLD = 48;
      const AXIS_LOCK = 12;

      const onTouchStart = (e) => {
        if (!MOBILE_MQ.matches || !this._dialog?.open || e.touches.length !== 1) return;
        this._swipeState = {
          startX: e.touches[0].clientX,
          startY: e.touches[0].clientY,
          axis: null,
          target: e.target,
        };
      };

      const onTouchMove = (e) => {
        const swipe = this._swipeState;
        if (!swipe || !MOBILE_MQ.matches || e.touches.length !== 1) return;

        const dx = e.touches[0].clientX - swipe.startX;
        const dy = e.touches[0].clientY - swipe.startY;

        if (!swipe.axis) {
          if (Math.abs(dx) < AXIS_LOCK && Math.abs(dy) < AXIS_LOCK) return;
          swipe.axis = Math.abs(dx) > Math.abs(dy) ? 'x' : 'y';
        }

        if (swipe.axis !== 'x') return;

        const onGallery = swipe.target.closest('[data-modal-image-wrapper]');
        const galleryImages = this._getActiveGalleryImages();
        const canSwipeGallery = Boolean(onGallery && galleryImages.length > 1);
        const canSwipeSlides = this._products.length > 1 && !this._isCharmsMode;

        if (canSwipeGallery || canSwipeSlides) e.preventDefault();
      };

      const onTouchEnd = (e) => {
        const swipe = this._swipeState;
        this._swipeState = null;
        if (!swipe || !MOBILE_MQ.matches || swipe.axis !== 'x') return;

        const dx = e.changedTouches[0].clientX - swipe.startX;
        if (Math.abs(dx) < THRESHOLD) return;

        const onGallery = swipe.target.closest('[data-modal-image-wrapper]');
        const galleryImages = this._getActiveGalleryImages();

        if (onGallery && galleryImages.length > 1) {
          this._stepGallery(dx < 0 ? 1 : -1);
          return;
        }

        if (this._products.length > 1 && !this._isCharmsMode) {
          this._goSlide(dx < 0 ? 1 : -1);
        }
      };

      this._viewport.addEventListener('touchstart', onTouchStart, { passive: true });
      this._viewport.addEventListener('touchmove', onTouchMove, { passive: false });
      this._viewport.addEventListener('touchend', onTouchEnd, { passive: true });
      this._viewport.addEventListener('touchcancel', onTouchEnd, { passive: true });
    }

    _getActiveGalleryContext() {
      const product = this._products[this._slideIndex];
      if (!product || !this._draft) return null;

      const slide = this._slides.get(normalizeProductId(product.id));
      if (!slide) return null;

      if (normalizeProductId(this._draft.productId) !== normalizeProductId(product.id)) {
        return { slide, product, images: [] };
      }

      let variant = null;
      if (this._isCharmsMode) {
        const activeId = this._draft.activeVariantId;
        if (activeId) {
          variant = product.variants.find(
            (v) => normalizeVariantId(v.id) === normalizeVariantId(activeId)
          );
        }
      } else {
        if (this._draft.variantId) {
          variant = product.variants.find(
            (v) => normalizeVariantId(v.id) === normalizeVariantId(this._draft.variantId)
          );
        }
        if (!variant) {
          variant = findVariant(
            product,
            this._draft.selectedOptions,
            this._variantDefault,
            getChainBlockSettings(this._block)
          );
        }
        if (this._block?.type === 'chain') {
          variant = resolveChainGalleryVariant(
            product,
            variant,
            this._block,
            this._variantDefault
          );
        }
      }

      const images = getVariantGalleryImages(
        variant,
        product,
        this._draft?.imagePrefersByoc
      );
      return { slide, product, images };
    }

    _getActiveGalleryImages() {
      return this._getActiveGalleryContext()?.images || [];
    }

    _setGalleryIndex(slide, index, images) {
      if (!slide || !images?.length) return;
      const idx = Math.max(0, Math.min(images.length - 1, index));
      if (this._draft) this._draft.galleryIndex = idx;

      const imgEl = slide.querySelector('[data-modal-image]');
      const dotsWrap = slide.querySelector('[data-modal-dots]');
      const wrapper = slide.querySelector('[data-modal-image-wrapper]');

      if (imgEl && images[idx]) {
        imgEl.src = images[idx];
        imgEl.removeAttribute('hidden');
      }

      dotsWrap?.querySelectorAll('.charm-builder-modal__dot').forEach((dot, i) => {
        dot.classList.toggle('is-active', i === idx);
      });

      if (wrapper) {
        if (images.length > 1) wrapper.setAttribute('data-modal-swipeable', '');
        else wrapper.removeAttribute('data-modal-swipeable');
      }
    }

    _stepGallery(delta) {
      const ctx = this._getActiveGalleryContext();
      if (!ctx || ctx.images.length <= 1) return;
      const current = this._draft?.galleryIndex || 0;
      this._setGalleryIndex(ctx.slide, current + delta, ctx.images);
    }

    _getBlock(blockId) {
      return this.data.blocks?.find((b) => b.id === blockId);
    }

    open({ blockId, productId }) {
      const block = this._getBlock(blockId);
      if (!block || !this._dialog) return;

      this._block = block;
      this._isCharmsMode = block.type === 'charms';
      this._variantDefault = this._resolveVariantDefault(block);

      if (this._isCharmsMode) {
        const product = this._findCharmProduct(block, productId);
        if (!product) return;
        this._products = [product];
        this._slideIndex = 0;
      } else {
        this._products = block.products || [];
        this._slideIndex = Math.max(
          0,
          this._products.findIndex((p) => p.id === productId)
        );
      }

      if (this._titleEl) this._titleEl.textContent = block.stepTitle || block.title || '';
      this._renderTrack();
      this._goToSlide(this._slideIndex, false);
      this._show();

      this.root.classList.add('is-modal-open');
      document.documentElement.classList.add('charm-builder-modal-open');
      this.root.dispatchEvent(new CustomEvent('charm-builder:modal-open'));
    }

    close() {
      this._draft = null;
      if (this._dialog?.open) this._dialog.close();
      this.root.classList.remove('is-modal-open');
      document.documentElement.classList.remove('charm-builder-modal-open');
      this.root.dispatchEvent(new CustomEvent('charm-builder:modal-close'));
    }

    _show() {
      if (!this._dialog.open) this._dialog.showModal();
    }

    _renderTrack() {
      if (!this._track) return;
      this._track.innerHTML = '';
      this._slides.clear();
      this._products.forEach((product) => {
        const slide = this._createSlide(product);
        this._track.appendChild(slide);
        this._slides.set(normalizeProductId(product.id), slide);
      });
      this._updateNavVisibility();
    }

    _createSlide(product) {
      if (this._isCharmsMode) return this._createCharmSlide(product);
      return this._createChainSlide(product);
    }

    _createChainSlide(product) {
      const slide = document.createElement('article');
      slide.className = 'charm-builder-modal__slide';
      slide.dataset.productId = String(product.id);

      slide.innerHTML = `
        <div class="charm-builder-modal__card">
          <div class="charm-builder-modal__gallery">
            <div class="charm-builder-modal__image-wrapper" data-modal-image-wrapper>
              <img class="charm-builder-modal__image" data-modal-image alt="" width="400" height="400" loading="lazy">
              <div class="charm-builder-modal__dots" data-modal-dots hidden></div>
            </div>
          </div>
          <div class="charm-builder-modal__info">
            <div class="charm-builder-modal__info-row">
              <h3 class="charm-builder-modal__product-title font-ivy" data-modal-product-title></h3>
              <div class="charm-builder-modal__price" data-modal-price></div>
            </div>
            <div class="charm-builder-modal__specs-host variant-content-switcher" data-variant-content-switcher></div>
            <div class="charm-builder-modal__variants" data-modal-variants-wrap hidden>
              <p class="charm-builder-modal__variants-label" data-modal-variants-label></p>
              <div class="charm-builder-modal__variant-row" data-modal-variant-options role="listbox"></div>
            </div>
            <button type="button" class="charm-builder-modal__add" data-modal-add disabled>
              ${this.data.labels?.modalAdd || 'ADD'}
            </button>
          </div>
        </div>
      `;

      const specsHost = slide.querySelector('[data-variant-content-switcher]');
      const specsTpl = document.getElementById(
        `CharmBuilderSpecs-${this.sectionId}-${product.id}`
      );
      if (specsTpl && specsHost) {
        const clone = specsTpl.cloneNode(true);
        clone.removeAttribute('id');
        specsHost.appendChild(clone);
      }

      const addBtn = slide.querySelector('[data-modal-add]');
      addBtn?.addEventListener('click', () => this._commitAdd(product.id));

      this._bindVariantOptions(slide, product);
      return slide;
    }

    _createCharmSlide(product) {
      const slide = document.createElement('article');
      slide.className = 'charm-builder-modal__slide charm-builder-modal__slide--charms';
      slide.dataset.productId = String(normalizeProductId(product.id));

      const qtySelectedLabel =
        this.data.labels?.quantitySelected || 'Quantity Selected';

      slide.innerHTML = `
        <div class="charm-builder-modal__card">
          <div class="charm-builder-modal__gallery">
            <div class="charm-builder-modal__image-wrapper" data-modal-image-wrapper>
              <img class="charm-builder-modal__image" data-modal-image alt="" width="400" height="400" loading="lazy" decoding="async">
              <div class="charm-builder-modal__dots" data-modal-dots hidden></div>
            </div>
          </div>
          <div class="charm-builder-modal__info">
            <div class="charm-builder-modal__info-row">
              <h3 class="charm-builder-modal__product-title font-ivy" data-modal-product-title></h3>
              <div class="charm-builder-modal__price" data-modal-price></div>
            </div>
            <div class="charm-builder-modal__specs-host variant-content-switcher" data-variant-content-switcher></div>
            <div class="charm-builder-modal__variants charm-builder-modal__variants--charms" data-modal-variants-wrap hidden>
              <p class="charm-builder-modal__variants-label" data-modal-variants-label></p>
              <div class="charm-builder-modal__letter-grid" data-modal-letter-grid role="listbox"></div>
              <div class="charm-builder-modal__color-host" data-modal-color-host hidden></div>
            </div>
            <div class="charm-builder-modal__qty-wrap" data-modal-qty-wrap hidden>
              <div class="charm-builder-modal__qty-selector" data-modal-qty-selector>
                <button type="button" class="charm-builder-modal__qty-btn" data-qty-decrease aria-label="Decrease quantity">−</button>
                <span class="charm-builder-modal__qty-value" data-qty-value>0</span>
                <button type="button" class="charm-builder-modal__qty-btn" data-qty-increase aria-label="Increase quantity">+</button>
              </div>
            </div>
            <div class="charm-builder-modal__qty-selected" data-modal-qty-selected-wrap hidden>
              <p class="charm-builder-modal__qty-selected-label">${qtySelectedLabel}</p>
              <ul class="charm-builder-modal__qty-selected-list" data-modal-qty-selected-list></ul>
            </div>
            <button type="button" class="charm-builder-modal__add" data-modal-add disabled>
              ${this.data.labels?.modalAdd || 'ADD'}
            </button>
          </div>
        </div>
      `;

      const specsHost = slide.querySelector('[data-variant-content-switcher]');
      const specsTpl = document.getElementById(
        `CharmBuilderSpecs-${this.sectionId}-${product.id}`
      );
      if (specsTpl && specsHost) {
        const clone = specsTpl.cloneNode(true);
        clone.removeAttribute('id');
        specsHost.appendChild(clone);
      }

      slide.querySelector('[data-modal-add]')?.addEventListener('click', () => {
        this._commitAdd(product.id);
      });

      slide.querySelector('[data-qty-decrease]')?.addEventListener('click', () => {
        this._adjustCharmQty(product.id, -1);
      });
      slide.querySelector('[data-qty-increase]')?.addEventListener('click', () => {
        this._adjustCharmQty(product.id, 1);
      });

      this._bindCharmPicker(slide, product);
      return slide;
    }

    _bindCharmPicker(slide, product) {
      if (usesCharmColorPicker(product)) {
        this._bindCharmColorPicker(slide, product);
      } else {
        this._bindCharmLetterGrid(slide, product);
      }
    }

    _bindCharmColorPicker(slide, product) {
      const wrap = slide.querySelector('[data-modal-variants-wrap]');
      const letterGrid = slide.querySelector('[data-modal-letter-grid]');
      const colorHost = slide.querySelector('[data-modal-color-host]');
      const labelEl = slide.querySelector('[data-modal-variants-label]');
      const pid = normalizeProductId(product.id);

      letterGrid?.setAttribute('hidden', '');
      if (!wrap || !colorHost) return;

      const tpl = document.getElementById(`CharmBuilderColors-${this.sectionId}-${pid}`);
      if (!tpl) {
        wrap.setAttribute('hidden', '');
        return;
      }

      colorHost.innerHTML = '';
      const clone = tpl.cloneNode(true);
      clone.removeAttribute('id');
      clone.removeAttribute('hidden');
      colorHost.appendChild(clone);
      colorHost.removeAttribute('hidden');
      wrap.removeAttribute('hidden');

      if (labelEl) {
        labelEl.textContent = product.colorOptionLabel || 'Color';
      }

      clone.querySelectorAll('input[type="radio"]').forEach((input) => {
        input.disabled = false;
        input.removeAttribute('disabled');
      });

      clone.querySelectorAll('.charm-builder-modal__color-item').forEach((item) => {
        const color = item.dataset.cbColorValue;
        const variantId = Number(item.dataset.cbVariantId);
        const label = item.querySelector('[data-option-value]');
        if (label) {
          label.setAttribute('role', 'button');
          label.tabIndex = 0;
        }
        const activate = () => {
          if (!this._draft || normalizeProductId(this._draft.productId) !== pid) return;
          if (item.classList.contains('is-sold-out')) return;
          const productDefault = this._getCharmVariantDefault(product);
          const v =
            product.variants.find((vr) => vr.id === variantId) ||
            findCharmVariantByColor(product, color, productDefault);
          if (!v || !v.available) return;
          this._draft.activeVariantId = v.id;
          this._draft.imagePrefersByoc = true;
          applyVariantToDraftOptions(this._draft, v);
          this._syncCharmSlideUI(pid);
        };
        item.addEventListener('click', (e) => {
          e.preventDefault();
          activate();
        });
        label?.addEventListener('keydown', (e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            activate();
          }
        });
      });
    }

    _bindVariantOptions(slide, product) {
      const wrap = slide.querySelector('[data-modal-variants-wrap]');
      const labelEl = slide.querySelector('[data-modal-variants-label]');
      const container = slide.querySelector('[data-modal-variant-options]');
      const picker = product.pickerOption;
      const chainSettings = getChainBlockSettings(this._block);
      const values = getPickerValues(product, this._variantDefault, chainSettings);

      if (!picker || values.length <= 1) {
        wrap?.setAttribute('hidden', '');
        return;
      }

      wrap?.removeAttribute('hidden');
      if (labelEl) labelEl.textContent = picker.label || picker.name;
      container.innerHTML = '';

      values.forEach((value) => {
        const btn = document.createElement('button');
        btn.type = 'button';
        btn.className = 'charm-builder-modal__variant-btn';
        btn.textContent = formatChainLengthDisplay(value, chainSettings);
        btn.setAttribute('aria-pressed', 'false');
        btn.dataset.optionPosition = String(picker.position);
        btn.dataset.optionValue = value;
        btn.addEventListener('click', () => {
          if (!this._draft || this._draft.productId !== product.id) return;
          this._draft.selectedOptions[`option${picker.position}`] = value;
          const variant = findVariant(
            product,
            this._draft.selectedOptions,
            this._variantDefault,
            chainSettings
          );
          this._draft.variantId = variant?.id ?? null;
          this._syncSlideUI(product.id);
        });
        container.appendChild(btn);
      });
    }

    _bindCharmLetterGrid(slide, product) {
      const wrap = slide.querySelector('[data-modal-variants-wrap]');
      const labelEl = slide.querySelector('[data-modal-variants-label]');
      const grid = slide.querySelector('[data-modal-letter-grid]');
      const colorHost = slide.querySelector('[data-modal-color-host]');
      const letters = getCharmLetterValues(product);
      const productDefault = this._getCharmVariantDefault(product);
      const picker = product.pickerOption;
      const pid = normalizeProductId(product.id);

      colorHost?.setAttribute('hidden', '');
      if (colorHost) colorHost.innerHTML = '';
      grid?.removeAttribute('hidden');

      if (letters.length <= 1) {
        wrap?.setAttribute('hidden', '');
        return;
      }

      wrap?.removeAttribute('hidden');
      if (labelEl) {
        labelEl.textContent = picker?.label || picker?.name || 'Letter';
      }
      if (!grid) return;
      grid.innerHTML = '';

      letters.forEach((letter) => {
        const variant = findCharmVariantByOption3(product, letter, productDefault);
        const btn = document.createElement('button');
        btn.type = 'button';
        btn.className = 'charm-builder-modal__letter-btn';
        btn.textContent = letter;
        btn.setAttribute('aria-pressed', 'false');
        btn.dataset.optionValue = letter;
        if (variant) btn.dataset.variantId = String(variant.id);
        btn.addEventListener('click', () => {
          if (!this._draft || normalizeProductId(this._draft.productId) !== pid) return;
          if (btn.classList.contains('is-sold-out')) return;
          const v =
            product.variants.find((vr) => vr.id === Number(btn.dataset.variantId)) ||
            findCharmVariantByOption3(product, letter, productDefault);
          if (!v || !v.available) return;
          this._draft.activeVariantId = v.id;
          this._draft.imagePrefersByoc = true;
          applyVariantToDraftOptions(this._draft, v);
          this._syncCharmSlideUI(pid);
        });
        grid.appendChild(btn);
      });
    }

    _getCharmDraftQty(variantId) {
      if (!this._draft?.quantities) return 0;
      return this._draft.quantities[String(variantId)] || 0;
    }

    _setCharmDraftQty(variantId, qty) {
      if (!this._draft) return;
      const key = String(variantId);
      if (qty <= 0) {
        delete this._draft.quantities[key];
      } else {
        this._draft.quantities[key] = qty;
      }
    }

    _getCharmMaxQty(variant) {
      if (!variant?.available) return 0;
      const inv = variant.inventory_quantity;
      if (typeof inv === 'number' && inv > 0) return inv;
      return 99;
    }

    _getCharmDraftTotalQty() {
      if (!this._draft?.quantities) return 0;
      return Object.values(this._draft.quantities).reduce((sum, n) => sum + n, 0);
    }

    _getVariantPickerLabel(variant, product) {
      if (!variant) return '';
      if (usesCharmColorPicker(product)) {
        return variant.option1 || variant.title;
      }
      return getOptionValue(variant, product?.pickerOption?.position) || variant.title;
    }

    _getCharmQuantitiesFromStore(productId) {
      const pid = normalizeProductId(productId);
      const lines = this.store.getLinesForProduct(this._block.id, pid);
      const quantities = {};
      lines.forEach((line) => {
        const key = String(normalizeVariantId(line.variantId));
        quantities[key] = (quantities[key] || 0) + 1;
      });
      return quantities;
    }

    _buildCharmStoreEntries(product, quantities) {
      const pid = normalizeProductId(product.id);
      const entries = [];
      Object.entries(quantities || {}).forEach(([variantId, quantity]) => {
        if (quantity <= 0) return;
        const variant = product.variants.find(
          (v) => normalizeVariantId(v.id) === normalizeVariantId(variantId)
        );
        if (!variant) return;
        entries.push({
          blockId: this._block.id,
          productId: pid,
          variantId: variant.id,
          quantity,
          price: variant.price,
          title: product.title,
          variantTitle: variant.title,
          image: getCharmVariantImage(variant, true, product),
        });
      });
      return entries;
    }

    _syncCharmDraftToStore(productId) {
      const pid = normalizeProductId(productId);
      const product = this._products.find((p) => normalizeProductId(p.id) === pid);
      if (!product || !this._draft || normalizeProductId(this._draft.productId) !== pid) {
        return;
      }
      const entries = this._buildCharmStoreEntries(product, this._draft.quantities);
      this.store.setCharmUnits(this._block.id, pid, entries);
    }

    _initCharmDraft(productId) {
      const pid = normalizeProductId(productId);
      const product = this._products.find((p) => normalizeProductId(p.id) === pid);
      if (!product) return;

      const quantities = this._getCharmQuantitiesFromStore(pid);

      this._draft = {
        blockId: this._block.id,
        blockType: 'charms',
        productId: pid,
        activeVariantId: null,
        selectedOptions: { option1: null, option2: null, option3: null },
        quantities: { ...quantities },
        galleryIndex: 0,
        imagePrefersByoc: false,
      };

      const productDefault = this._getCharmVariantDefault(product);
      const storedVariantIds = Object.keys(quantities);
      let defaultVariant = null;

      if (storedVariantIds.length) {
        const firstStoredId = normalizeVariantId(storedVariantIds[0]);
        defaultVariant = product.variants.find(
          (v) => normalizeVariantId(v.id) === firstStoredId
        );
      }
      if (!defaultVariant) {
        defaultVariant = getDefaultCharmVariant(product, productDefault);
      }
      if (defaultVariant) {
        this._draft.activeVariantId = defaultVariant.id;
        applyVariantToDraftOptions(this._draft, defaultVariant);
      }

      this._syncCharmSlideUI(pid);
    }

    _syncCharmSlideUI(productId) {
      const pid = normalizeProductId(productId);
      const product = this._products.find((p) => normalizeProductId(p.id) === pid);
      const slide = this._slides.get(pid);
      if (!product || !slide || !this._draft || normalizeProductId(this._draft.productId) !== pid) {
        return;
      }

      const productDefault = this._getCharmVariantDefault(product);
      let variant =
        product.variants.find(
          (v) => normalizeVariantId(v.id) === normalizeVariantId(this._draft.activeVariantId)
        ) || null;
      if (!variant) {
        variant = getDefaultCharmVariant(product, productDefault);
        if (variant) {
          this._draft.activeVariantId = variant.id;
          applyVariantToDraftOptions(this._draft, variant);
        }
      }
      if (!variant && product.variants.length) {
        variant = product.variants.find((v) => v.available) || product.variants[0];
        if (variant) {
          this._draft.activeVariantId = variant.id;
          applyVariantToDraftOptions(this._draft, variant);
        }
      }

      const titleEl = slide.querySelector('[data-modal-product-title]');
      const imgEl = slide.querySelector('[data-modal-image]');
      const addBtn = slide.querySelector('[data-modal-add]');
      const qtyWrap = slide.querySelector('[data-modal-qty-wrap]');
      const qtyValueEl = slide.querySelector('[data-qty-value]');
      const qtyDecrease = slide.querySelector('[data-qty-decrease]');
      const qtyIncrease = slide.querySelector('[data-qty-increase]');

      if (titleEl) titleEl.textContent = product.title;

      if (variant) {
        this._renderPrice(slide, product.id, variant.id);
        if (imgEl) imgEl.alt = product.title;
        if (charmShowsVariantPicker(product)) {
          this._renderCharmImage(slide, variant, product);
        } else {
          this._renderGalleryDots(slide, variant, product);
        }
        this._switchSpecs(slide, variant.id);
        this._notifyCardVariant(product.id, variant.id);
      } else if (imgEl && product.featuredImage) {
        imgEl.src = product.featuredImage;
        imgEl.alt = product.title;
        imgEl.removeAttribute('hidden');
      }

      this._syncCharmPickerButtons(slide, product);

      const showQty = Boolean(variant);
      qtyWrap?.toggleAttribute('hidden', !showQty);

      if (variant && qtyValueEl) {
        const qty = this._getCharmDraftQty(variant.id);
        qtyValueEl.textContent = String(qty);
        const max = this._getCharmMaxQty(variant);
        if (qtyDecrease) qtyDecrease.disabled = qty <= 0;
        if (qtyIncrease) qtyIncrease.disabled = qty >= max;
      }

      this._renderCharmQuantitySelected(slide, product);

      const totalQty = this._getCharmDraftTotalQty();
      if (addBtn) addBtn.disabled = totalQty === 0;
    }

    _syncCharmPickerButtons(slide, product) {
      if (!this._draft) return;

      if (usesCharmColorPicker(product)) {
        const productDefault = this._getCharmVariantDefault(product);
        slide.querySelectorAll('.charm-builder-modal__color-item').forEach((item) => {
          const variantId = normalizeVariantId(item.dataset.cbVariantId);
          const color = item.dataset.cbColorValue;
          const variant =
            product.variants.find((v) => normalizeVariantId(v.id) === variantId) ||
            findCharmVariantByColor(product, color, productDefault);
          const isActive =
            variant && normalizeVariantId(this._draft.activeVariantId) === normalizeVariantId(variant.id);
          const hasQty = variant ? this._getCharmDraftQty(variant.id) > 0 : false;
          const domAvailable = item.dataset.cbColorAvailable !== 'false';
          const soldOut = variant ? !variant.available : !domAvailable;
          const label = item.querySelector('.color-swatch, .block-swatch, [data-option-value]');
          const input = item.querySelector('input[type="radio"]');
          label?.classList.toggle('is-selected', Boolean(isActive));
          label?.classList.toggle('is-disabled', soldOut);
          item.classList.toggle('is-sold-out', soldOut);
          item.classList.toggle('has-qty', hasQty);
          item.toggleAttribute('aria-disabled', soldOut);
          if (input) {
            input.disabled = false;
            input.removeAttribute('disabled');
            input.checked = Boolean(isActive);
          }
        });
        return;
      }

      if (!getCharmLetterValues(product).length) return;

      const productDefault = this._getCharmVariantDefault(product);
      slide.querySelectorAll('.charm-builder-modal__letter-btn').forEach((btn) => {
        const letter = btn.dataset.optionValue;
        const variant =
          product.variants.find((v) => v.id === Number(btn.dataset.variantId)) ||
          findCharmVariantByOption3(product, letter, productDefault);
        const isActive = variant && this._draft.activeVariantId === variant.id;
        const hasQty = variant ? this._getCharmDraftQty(variant.id) > 0 : false;
        const soldOut = !variant?.available;

        btn.classList.toggle('is-selected', isActive);
        btn.classList.toggle('has-qty', hasQty);
        btn.classList.toggle('is-sold-out', soldOut);
        btn.setAttribute('aria-pressed', String(Boolean(isActive)));
        btn.disabled = false;
        btn.toggleAttribute('aria-disabled', soldOut);
        if (variant) btn.dataset.variantId = String(variant.id);
      });
    }

    _renderQtySelectedThumb(thumb, slide, product, variant) {
      if (!charmShowsVariantPicker(product)) {
        const imgSrc = getCharmVariantImage(variant, true, product);
        if (imgSrc) {
          const img = document.createElement('img');
          img.src = imgSrc;
          img.alt = product.title || '';
          img.className = 'charm-builder-modal__qty-selected-image';
          img.loading = 'lazy';
          img.decoding = 'async';
          thumb.appendChild(img);
          return;
        }
        thumb.textContent = this._getVariantPickerLabel(variant, product);
        return;
      }

      if (usesCharmColorPicker(product)) {
        const colorItem = slide.querySelector(
          `.charm-builder-modal__color-item[data-cb-variant-id="${variant.id}"]`
        );
        const swatch = colorItem?.querySelector('.color-swatch');
        if (swatch) {
          const mini = swatch.cloneNode(true);
          mini.classList.add('charm-builder-modal__qty-selected-swatch');
          mini.classList.remove('is-selected', 'is-disabled');
          thumb.appendChild(mini);
          return;
        }
        thumb.textContent = variant.option1 || '';
        return;
      }

      thumb.textContent = this._getVariantPickerLabel(variant, product);
    }

    _renderCharmQuantitySelected(slide, product) {
      const wrap = slide.querySelector('[data-modal-qty-selected-wrap]');
      const list = slide.querySelector('[data-modal-qty-selected-list]');
      if (!wrap || !list || !this._draft) return;

      const entries = Object.entries(this._draft.quantities).filter(([, qty]) => qty > 0);
      const showWrap = entries.length > 0 && charmShowsVariantPicker(product);
      wrap.toggleAttribute('hidden', !showWrap);
      list.innerHTML = '';
      const pid = normalizeProductId(product.id);

      entries.forEach(([variantId, qty]) => {
        const variant = product.variants.find((v) => v.id === Number(variantId));
        if (!variant) return;

        const li = document.createElement('li');
        li.className = 'charm-builder-modal__qty-selected-item';

        const thumb = document.createElement('span');
        thumb.className = 'charm-builder-modal__qty-selected-thumb';
        this._renderQtySelectedThumb(thumb, slide, product, variant);

        const label = document.createElement('span');
        label.className = 'charm-builder-modal__qty-selected-text';
        label.textContent = `Quantity: ${qty}`;

        const removeBtn = document.createElement('button');
        removeBtn.type = 'button';
        removeBtn.className = 'charm-builder-modal__qty-selected-remove';
        removeBtn.setAttribute('aria-label', 'Remove');
        removeBtn.innerHTML =
          '<svg width="16" height="16" viewBox="0 0 24 24" fill="none" aria-hidden="true"><path d="M3 6h18M8 6V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2m3 0v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6h14zM10 11v6M14 11v6" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/></svg>';
        removeBtn.addEventListener('click', () => {
          this._setCharmDraftQty(variant.id, 0);
          this._syncCharmDraftToStore(pid);
          if (this._draft.activeVariantId === variant.id) {
            const qtyEl = slide.querySelector('[data-qty-value]');
            if (qtyEl) qtyEl.textContent = '0';
          }
          this._syncCharmSlideUI(pid);
        });

        li.append(thumb, label, removeBtn);
        list.appendChild(li);
      });
    }

    _adjustCharmQty(productId, delta) {
      const pid = normalizeProductId(productId);
      const product = this._products.find((p) => normalizeProductId(p.id) === pid);
      if (!product || !this._draft || normalizeProductId(this._draft.productId) !== pid) {
        return;
      }

      const variant = product.variants.find(
        (v) => normalizeVariantId(v.id) === normalizeVariantId(this._draft.activeVariantId)
      );
      if (!variant) return;

      const current = this._getCharmDraftQty(variant.id);
      const max = this._getCharmMaxQty(variant);
      const next = Math.min(max, Math.max(0, current + delta));
      this._setCharmDraftQty(variant.id, next);
      this._syncCharmSlideUI(pid);
    }

    _commitCharmAdd(productId) {
      const pid = normalizeProductId(productId);
      const product = this._products.find((p) => normalizeProductId(p.id) === pid);
      if (!product || !this._draft || normalizeProductId(this._draft.productId) !== pid) {
        return;
      }

      const totalQty = this._getCharmDraftTotalQty();
      if (totalQty === 0) return;

      this._syncCharmDraftToStore(pid);
      this.close();
    }

    _initDraft(productId) {
      if (this._isCharmsMode) {
        this._initCharmDraft(productId);
        return;
      }

      const product = this._products.find((p) => p.id === productId);
      if (!product) return;

      const existing = this.store.getLinesForProduct(this._block.id, productId)[0];
      const selectedOptions = { option1: null, option2: null, option3: null };
      let variantId = null;
      const chainSettings = getChainBlockSettings(this._block);
      const isChain = this._block?.type === 'chain';

      if (existing) {
        variantId = existing.variantId;
        const v = product.variants.find((vr) => vr.id === variantId);
        if (v && !(isChain && isChainLengthExcluded(v, product, chainSettings))) {
          selectedOptions.option1 = v.option1;
          selectedOptions.option2 = v.option2;
          selectedOptions.option3 = v.option3;
        } else {
          variantId = null;
        }
      } else if (!variantNeedsSelection(product, this._variantDefault, chainSettings)) {
        if (product.pickerOption) {
          const values = getPickerValues(product, this._variantDefault, chainSettings);
          if (values.length === 1) {
            selectedOptions[`option${product.pickerOption.position}`] = values[0];
          }
        }
        const v = isChain
          ? getDefaultChainVariant(product, this._variantDefault, chainSettings)
          : findVariant(product, selectedOptions, this._variantDefault, chainSettings);
        variantId = v?.id ?? null;
        if (v) {
          selectedOptions.option1 = v.option1;
          selectedOptions.option2 = v.option2;
          selectedOptions.option3 = v.option3;
        }
      } else if (product.pickerOption) {
        const pos = product.pickerOption.position;
        const key = `option${pos}`;
        const firstAvailable = getPickerValues(product, this._variantDefault, chainSettings).find((val) => {
          const hypot = { ...selectedOptions, [key]: val };
          const v = findVariant(product, hypot, this._variantDefault, chainSettings);
          return v?.available;
        });
        if (firstAvailable) {
          selectedOptions[key] = firstAvailable;
          const v = findVariant(product, selectedOptions, this._variantDefault, chainSettings);
          variantId = v?.id ?? null;
        }
      }

      this._draft = {
        blockId: this._block.id,
        blockType: this._block.type,
        productId,
        variantId,
        selectedOptions,
        galleryIndex: 0,
      };
      this._syncSlideUI(productId);
    }

    _syncSlideUI(productId) {
      if (this._isCharmsMode) {
        this._syncCharmSlideUI(productId);
        return;
      }

      const product = this._products.find((p) => p.id === productId);
      const slide = this._slides.get(productId);
      if (!product || !slide || !this._draft || this._draft.productId !== productId) return;

      let variant =
        product.variants.find((v) => v.id === this._draft.variantId) || null;
      const chainSettings = getChainBlockSettings(this._block);
      const isChain = this._block?.type === 'chain';

      if (variant && isChain && isChainLengthExcluded(variant, product, chainSettings)) {
        variant = null;
        this._draft.variantId = null;
      }

      if (!variant && variantNeedsSelection(product, this._variantDefault, chainSettings)) {
        variant = findVariant(
          product,
          this._draft.selectedOptions,
          this._variantDefault,
          chainSettings
        );
        if (variant) this._draft.variantId = variant.id;
      }
      if (!variant) {
        variant = isChain
          ? getDefaultChainVariant(product, this._variantDefault, chainSettings)
          : getDefaultCharmVariant(product, this._variantDefault);
        if (variant) {
          this._draft.variantId = variant.id;
          applyVariantToDraftOptions(this._draft, variant);
        }
      }

      const galleryVariant = resolveChainGalleryVariant(
        product,
        variant,
        this._block,
        this._variantDefault
      );

      const titleEl = slide.querySelector('[data-modal-product-title]');
      const priceEl = slide.querySelector('[data-modal-price]');
      const imgEl = slide.querySelector('[data-modal-image]');
      const addBtn = slide.querySelector('[data-modal-add]');

      if (titleEl) titleEl.textContent = product.title;
      this._renderPrice(slide, product.id, variant?.id);
      if (imgEl && galleryVariant) {
        const imgSrc = getCharmVariantImage(galleryVariant, false, product);
        if (imgSrc) {
          imgEl.src = imgSrc;
          imgEl.removeAttribute('hidden');
        }
        imgEl.alt = product.title;
      } else if (imgEl && product.featuredImage) {
        imgEl.src = product.featuredImage;
        imgEl.alt = product.title;
        imgEl.removeAttribute('hidden');
      }

      this._renderGalleryDots(slide, galleryVariant, product);
      this._switchSpecs(slide, variant?.id);
      this._syncVariantButtons(slide, product);
      if (variant?.id) this._notifyCardVariant(product.id, variant.id);

      const canAdd =
        variant &&
        variant.available &&
        (!variantNeedsSelection(product, this._variantDefault, chainSettings) ||
          this._draft.variantId != null);
      if (addBtn) addBtn.disabled = !canAdd;
    }

    _notifyCardVariant(productId, variantId) {
      if (this.root?.updateCardVariant) {
        this.root.updateCardVariant(productId, variantId, this._block?.id);
      } else if (this.root?.updateCardPrice) {
        this.root.updateCardPrice(productId, variantId);
      }
    }

    _renderCharmImage(slide, variant, product) {
      const dotsWrap = slide.querySelector('[data-modal-dots]');
      const imgEl = slide.querySelector('[data-modal-image]');
      if (dotsWrap) {
        dotsWrap.hidden = true;
        dotsWrap.innerHTML = '';
      }
      if (!imgEl) return;
      const imgSrc = getCharmVariantImage(
        variant,
        this._draft?.imagePrefersByoc,
        product
      );
      if (imgSrc) {
        imgEl.src = imgSrc;
        imgEl.removeAttribute('hidden');
      }
    }

    _renderGalleryDots(slide, variant, product) {
      const dotsWrap = slide.querySelector('[data-modal-dots]');
      const imgEl = slide.querySelector('[data-modal-image]');
      const wrapper = slide.querySelector('[data-modal-image-wrapper]');
      if (!dotsWrap || !imgEl) return;

      const display = getVariantGalleryImages(
        variant,
        product,
        this._draft?.imagePrefersByoc
      );
      dotsWrap.innerHTML = '';

      if (wrapper) wrapper.removeAttribute('data-modal-swipeable');

      if (!display.length) {
        dotsWrap.hidden = true;
        return;
      }

      if (display.length <= 1) {
        dotsWrap.hidden = true;
        imgEl.src = display[0];
        imgEl.removeAttribute('hidden');
        return;
      }

      if (wrapper) wrapper.setAttribute('data-modal-swipeable', '');

      dotsWrap.hidden = false;
      display.forEach((url, index) => {
        const dot = document.createElement('button');
        dot.type = 'button';
        dot.className = 'charm-builder-modal__dot';
        dot.setAttribute('aria-label', `Image ${index + 1}`);
        if (index === (this._draft?.galleryIndex || 0)) dot.classList.add('is-active');
        dot.addEventListener('click', () => {
          this._setGalleryIndex(slide, index, display);
        });
        dotsWrap.appendChild(dot);
      });
      const start = Math.min(this._draft?.galleryIndex || 0, display.length - 1);
      this._setGalleryIndex(slide, start, display);
    }

    _renderPrice(slide, productId, variantId) {
      const priceEl = slide.querySelector('[data-modal-price]');
      if (!priceEl) return;

      const tpl = this.root.querySelector(
        `[data-cb-price][data-cb-price-product="${productId}"][data-cb-price-variant="${variantId}"]`
      );

      if (tpl) {
        priceEl.innerHTML = tpl.innerHTML;
        return;
      }

      const product = this._products.find((p) => normalizeProductId(p.id) === normalizeProductId(productId));
      const variant = product?.variants.find(
        (v) => normalizeVariantId(v.id) === normalizeVariantId(variantId)
      );
      if (variant) {
        priceEl.textContent = formatMoney(variant.price, this.data.moneyFormat);
      }
    }

    _switchSpecs(slide, variantId) {
      const switcher = slide.querySelector('[data-variant-content-switcher]');
      if (!switcher || !variantId) return;
      switcher.querySelectorAll('[data-variant-id]').forEach((panel) => {
        const id = Number(panel.getAttribute('data-variant-id'));
        panel.toggleAttribute('hidden', id !== variantId);
      });
    }

    _syncVariantButtons(slide, product) {
      if (!this._draft || !product.pickerOption) return;

      const pos = product.pickerOption.position;
      slide.querySelectorAll('.charm-builder-modal__variant-btn').forEach((btn) => {
        const val = btn.dataset.optionValue;
        const selected = this._draft.selectedOptions[`option${pos}`] === val;
        btn.classList.toggle('is-selected', selected);
        btn.setAttribute('aria-pressed', String(selected));

        const hypot = { ...this._draft.selectedOptions, [`option${pos}`]: val };
        const variant = findVariant(
          product,
          hypot,
          this._variantDefault,
          getChainBlockSettings(this._block)
        );
        btn.disabled = variant ? !variant.available : false;
      });
    }

    _commitAdd(productId) {
      if (this._isCharmsMode) {
        this._commitCharmAdd(productId);
        return;
      }

      const product = this._products.find((p) => p.id === productId);
      if (!product || !this._draft || this._draft.productId !== productId) return;

      const variant = product.variants.find((v) => v.id === this._draft.variantId);
      const chainSettings = getChainBlockSettings(this._block);
      if (
        !variant ||
        !variant.available ||
        (this._block?.type === 'chain' &&
          isChainLengthExcluded(variant, product, chainSettings))
      ) {
        return;
      }

      const existing = this.store.getLinesForProduct(this._block.id, productId)[0];

      this.store.upsertSingleProduct({
        lineId: existing?.lineId,
        blockId: this._block.id,
        blockType: this._block.type,
        productId: product.id,
        variantId: variant.id,
        price: variant.price,
        title: product.title,
        variantTitle: variant.title,
        image: variant.image,
        quantity: 1,
      });

      this.close();
    }

    _goSlide(delta) {
      const next = this._slideIndex + delta;
      if (next < 0 || next >= this._products.length) return;
      this._goToSlide(next, true);
    }

    _goToSlide(index, animate) {
      this._slideIndex = index;
      const product = this._products[index];
      if (!product) return;

      this._initDraft(product.id);
      if (this._track) {
        this._track.style.transform = `translateX(-${index * 100}%)`;
        Array.from(this._track.children).forEach((slide, i) => {
          slide.toggleAttribute('inert', i !== index);
        });
      }
      this._updateNavVisibility();
    }

    _updateNavVisibility() {
      if (this._isCharmsMode) {
        this._prevBtn?.setAttribute('hidden', '');
        this._nextBtn?.setAttribute('hidden', '');
        this._viewport?.removeAttribute('data-modal-swipeable-slides');
        this._updatePagination();
        return;
      }
      const multi = this._products.length > 1;
      if (multi) this._viewport?.setAttribute('data-modal-swipeable-slides', '');
      else this._viewport?.removeAttribute('data-modal-swipeable-slides');
      this._prevBtn?.toggleAttribute('hidden', !multi || this._slideIndex <= 0);
      this._nextBtn?.toggleAttribute('hidden', !multi || this._slideIndex >= this._products.length - 1);
      this._updatePagination();
    }

    _updatePagination() {
      const total = this._isCharmsMode ? 0 : this._products.length;

      if (this._counterEl) {
        this._counterEl.textContent = total > 1 ? `${this._slideIndex + 1}/${total}` : '';
      }

      if (this._dotsEl) {
        if (this._dotsEl.children.length !== total) {
          this._dotsEl.innerHTML = '';
          for (let i = 0; i < total; i += 1) {
            const dot = document.createElement('span');
            dot.className = 'charm-builder-modal__slide-dot';
            this._dotsEl.appendChild(dot);
          }
        }
        Array.from(this._dotsEl.children).forEach((dot, i) => {
          dot.classList.toggle('is-active', i === this._slideIndex);
        });
      }
    }
  }

  class CharmBuilderAccordionGroup extends HTMLElement {
    connectedCallback() {
      this._onToggle = this._handleToggle.bind(this);
      this._onLayoutChange = () => this._applyLayout();
      this._bindBlockAccordions();
      MOBILE_MQ.addEventListener('change', this._onLayoutChange);
      this._applyLayout();
    }

    disconnectedCallback() {
      this._blockAccordions().forEach((el) => el.removeEventListener('toggle', this._onToggle));
      MOBILE_MQ.removeEventListener('change', this._onLayoutChange);
    }

    _blockAccordions() {
      return Array.from(this.querySelectorAll('details.accordion')).filter((el) =>
        el.querySelector('[data-charm-builder-block]')
      );
    }

    _bindBlockAccordions() {
      this._blockAccordions().forEach((el) => el.addEventListener('toggle', this._onToggle));
    }

    _applyLayout() {
      const accordions = this._blockAccordions();
      if (!accordions.length) return;

      if (MOBILE_MQ.matches) {
        accordions.forEach((el, index) => {
          el.open = index === 0;
        });
      } else {
        accordions.forEach((el) => {
          el.open = true;
        });
      }
    }

    advanceFromBlock(blockId) {
      if (!MOBILE_MQ.matches) return;
      const accordions = this._blockAccordions();
      const currentIdx = accordions.findIndex((el) =>
        el.querySelector(`[data-charm-builder-block][data-block-id="${blockId}"]`)
      );
      if (currentIdx === -1) return;

      accordions[currentIdx].open = false;
      const next = accordions[currentIdx + 1];
      if (next) {
        next.open = true;
        next.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
      }
    }

    _handleToggle(event) {
      if (!MOBILE_MQ.matches) return;
      const target = event.target;
      if (!target.matches('details.accordion') || !target.querySelector('[data-charm-builder-block]')) {
        return;
      }
      if (!target.open) return;
      this._blockAccordions().forEach((details) => {
        if (details !== target) details.open = false;
      });
    }
  }

  function cartRoute(path) {
    const root = window.Shopify?.routes?.root || '/';
    return `${root}${path}`.replace(/\/{2,}/g, '/').replace(':/', '://');
  }

  class CharmBuilderRoot extends HTMLElement {
    connectedCallback() {
      this._sectionId = this.dataset.sectionId;
      this._data = this._readData();
      this._moneyFormat = this._data?.moneyFormat;

      this._store = new CharmBundleStore(this._sectionId, this._data?.pageHandle);

      this._reviewList = this.querySelector('[data-review-list]');
      this._reviewEmpty = this.querySelector('[data-review-empty]');
      this._stickyEl = this.querySelector('[data-charm-builder-sticky]');
      this._stickyList = this.querySelector('[data-sticky-list]');
      this._totalEls = this.querySelectorAll('[data-bundle-total]');
      this._addBundleBtns = this.querySelectorAll('[data-add-bundle]');
      this._blockCountEls = this.querySelectorAll('[data-cb-count]');
      this._reviewCountEl = this.querySelector('[data-cb-review-count]');
      this._addingBundle = false;

      this._modal = new CharmBuilderModal(this, this._store, this._data);

      this._bindCards();
      this._bindCharmsTabs();
      this._bindReview();
      this._bindStore();
      this._bindPageLifecycle();
      this._store.load();

      this._renderReview(this._store.getState());
      requestAnimationFrame(() => this._syncInitialChainCards());

      this.addEventListener('charm-builder:modal-open', () => {
        this._updateStickyBar(this._store.getState());
        this._syncInitialChainCards();
      });
      this.addEventListener('charm-builder:modal-close', () => {
        this._updateStickyBar(this._store.getState());
      });

      document.addEventListener('shopify:section:load', this._onSectionLoad);
    }

    disconnectedCallback() {
      document.removeEventListener('shopify:section:load', this._onSectionLoad);
      this._store?.removeEventListener('charm-builder:change', this._onStoreChange);
    }

    _readData() {
      const el = document.getElementById(`CharmBuilderData-${this._sectionId}`);
      if (!el) return null;
      try {
        return JSON.parse(el.textContent);
      } catch (e) {
        console.error('[CharmBuilder] Invalid JSON bootstrap', e);
        return null;
      }
    }

    _getBlock(blockId) {
      return this._data?.blocks?.find((b) => b.id === blockId);
    }

    _getProduct(blockId, productId) {
      const block = this._getBlock(blockId);
      if (!block) return null;
      if (block.type === 'charms') {
        const id = Number(productId);
        for (const col of block.collections || []) {
          const p = col.products?.find((pr) => Number(pr.id) === id);
          if (p) return p;
        }
        return null;
      }
      return block.products?.find((p) => Number(p.id) === Number(productId));
    }

    _bindStore() {
      this._onStoreChange = (event) => {
        this._renderReview(event.detail.state);
        this._syncCardSelection(event.detail.state);
        this._syncCardPrices(event.detail.state);

        const { type, payload } = event.detail;
        if (type === 'upsert' && payload?.line?.blockId) {
          this._advanceMobileBlock(payload.line.blockId);
        } else if (type === 'add-charms' && payload?.entries?.[0]?.blockId) {
          this._advanceMobileBlock(payload.entries[0].blockId);
        }
      };
      this._store.addEventListener('charm-builder:change', this._onStoreChange);
    }

    updateCardPrice(productId, variantId) {
      this.updateCardVariant(productId, variantId);
    }

    updateCardVariant(productId, variantId, blockId) {
      const pid = normalizeProductId(productId);
      const vid = normalizeVariantId(variantId);
      if (!pid || !vid) return;

      const tpl = this.querySelector(
        `[data-cb-price][data-cb-price-product="${pid}"][data-cb-price-variant="${vid}"]`
      );

      const block = blockId ? this._getBlock(blockId) : null;
      const product = blockId ? this._getProduct(blockId, pid) : null;
      const variant = product?.variants?.find((v) => normalizeVariantId(v.id) === vid);

      this.querySelectorAll(`[data-charm-builder-card][data-product-id="${pid}"]`).forEach((card) => {
        if (blockId && card.dataset.blockId !== blockId) return;

        const priceEl = card.querySelector('[data-product-price]');
        if (priceEl) {
          if (tpl) {
            priceEl.innerHTML = tpl.innerHTML;
          } else if (variant) {
            priceEl.textContent = formatMoney(variant.price, this._moneyFormat);
          }
          priceEl.dataset.activeVariantId = String(vid);
        }

        card.dataset.defaultVariantId = String(vid);

        if (
          variant &&
          (card.dataset.blockType === 'chain' || card.dataset.blockType === 'connector')
        ) {
          this._updateChainCardImage(card, variant, product);
        }
      });
    }

    _updateChainCardImage(card, variant, product) {
      const img = card.querySelector('.charm-builder-card__image');
      if (!img) return;

      const src =
        product?.cardImage ||
        (variant ? getModalPrimaryImageSrc(variant, product, false) : '');
      if (!src) return;

      img.removeAttribute('srcset');
      img.removeAttribute('sizes');
      img.src = src;
      img.alt = product?.title || '';
      img.removeAttribute('hidden');
    }

    _syncInitialChainCards() {
      this._data?.blocks?.forEach((block) => {
        if (block.type !== 'chain') return;
        const chainSettings = getChainBlockSettings(block);
        const variantDefault = (block.variantDefault || '').toLowerCase().trim();

        (block.products || []).forEach((product) => {
          const cardVariantId = product.cardVariantId
            ? normalizeVariantId(product.cardVariantId)
            : null;
          const cardVariant =
            (cardVariantId &&
              product.variants?.find((v) => normalizeVariantId(v.id) === cardVariantId)) ||
            resolveChainCardVariant(product, block) ||
            getDefaultChainVariant(product, variantDefault, chainSettings);
          if (!cardVariant) return;
          this.updateCardVariant(product.id, cardVariant.id, block.id);
        });
      });
    }

    _syncCardPrices(state) {
      const lastVariantByProduct = new Map();
      state.lines.forEach((line) => {
        if (line.productId && line.variantId) {
          lastVariantByProduct.set(`${line.blockId}:${line.productId}`, {
            productId: line.productId,
            variantId: line.variantId,
            blockId: line.blockId,
          });
        }
      });
      lastVariantByProduct.forEach(({ productId, variantId, blockId }) => {
        this.updateCardVariant(productId, variantId, blockId);
      });
    }

    _advanceMobileBlock(blockId) {
      this.querySelector('charm-builder-accordion-group')?.advanceFromBlock?.(blockId);
    }

    _bindPageLifecycle() {
      const pathKey = `${STORAGE_PREFIX}last-path`;
      const currentPath = window.location.pathname;
      const lastPath = sessionStorage.getItem(pathKey);

      if (lastPath && lastPath !== currentPath) {
        try {
          sessionStorage.removeItem(this._store._storageKey);
        } catch (e) {
          /* ignore */
        }
        this._store.lines = [];
      }

      sessionStorage.setItem(pathKey, currentPath);
    }

    _onSectionLoad = (event) => {
      if (event.detail?.sectionId === this._sectionId) {
        this._data = this._readData();
        this._modal = new CharmBuilderModal(this, this._store, this._data);
        this._store.load();
        this._renderReview(this._store.getState());
        requestAnimationFrame(() => this._syncInitialChainCards());
      }
    };

    _bindCards() {
      // Bind on the input itself (not the label) so both the native label-click
      // forwarding (mouse) and native Space activation (keyboard) funnel through
      // the same click event on the control, and preventDefault reliably stops
      // the checkbox from toggling on its own — checked state stays store-driven
      // (see _syncCardSelection); selecting always requires the modal for variant/qty.
      this.querySelectorAll('[data-charm-builder-card-input]').forEach((input) => {
        input.addEventListener('click', (e) => {
          e.preventDefault();
          const card = input.closest('[data-charm-builder-card]');
          if (card) this._onCardClick(card);
        });
        // Native checkboxes only activate on Space, not Enter. These cards opened
        // on Enter before the a11y fix, so restore it alongside native Space/click.
        input.addEventListener('keydown', (e) => {
          if (e.key !== 'Enter') return;
          e.preventDefault();
          const card = input.closest('[data-charm-builder-card]');
          if (card) this._onCardClick(card);
        });
        // The input is visually hidden and pulled out of the flex flow, so the
        // browser's automatic scroll-into-view-on-focus uses its (tiny, offset)
        // geometry instead of the visible card's — scroll the real card instead.
        input.addEventListener('focus', () => {
          const card = input.closest('[data-charm-builder-card]');
          card?.scrollIntoView({ block: 'nearest', inline: 'nearest' });
        });
      });
    }

    _onCardClick(card) {
      if (card.classList.contains('charm-builder-card--sold-out')) return;

      const productId = Number(card.dataset.productId);
      const blockId = card.dataset.blockId;
      const blockType = card.dataset.blockType;

      this._modal?.open({ blockId, productId });
    }

    _bindCharmsTabs() {
      const tabs = this.querySelectorAll('[data-charms-tab]');
      if (!tabs.length) return;

      tabs.forEach((tab) => {
        tab.addEventListener('click', () => {
          const handle = tab.dataset.charmsTab;
          const block = tab.closest('[data-charm-builder-block]');
          if (!block) return;

          block.querySelectorAll('[data-charms-tab]').forEach((t) => {
            t.classList.toggle('is-active', t === tab);
            t.setAttribute('aria-selected', t === tab ? 'true' : 'false');
          });

          block.querySelectorAll('[data-charms-panel]').forEach((panel) => {
            const show = panel.dataset.charmsPanel === handle;
            panel.classList.toggle('hidden', !show);
            panel.toggleAttribute('hidden', !show);
          });
        });
      });
    }

    _bindReview() {
      this._addBundleBtns.forEach((btn) => {
        btn.addEventListener('click', () => this._addBundleToCart());
      });
    }

    _validateBundle() {
      const state = this._store.getState();
      return {
        valid: state.count > 0,
        missing: [],
        reason: state.count > 0 ? null : 'empty',
      };
    }

    _buildCartItems() {
      const counts = new Map();
      this._store.lines.forEach((line) => {
        if (!line.variantId) return;
        const id = Number(line.variantId);
        counts.set(id, (counts.get(id) || 0) + 1);
      });
      return Array.from(counts.entries()).map(([id, quantity]) => ({ id, quantity }));
    }

    _clearBundleError() {
      const el = this.querySelector('[data-bundle-error]');
      if (el) {
        el.textContent = '';
        el.hidden = true;
      }
    }

    _showBundleError(message) {
      let el = this.querySelector('[data-bundle-error]');
      if (!el) {
        el = document.createElement('p');
        el.className = 'charm-builder-review__error';
        el.dataset.bundleError = '';
        el.setAttribute('role', 'alert');
        const footer =
          this.querySelector('.charm-builder-review__footer') ||
          this.querySelector('.charm-builder-sticky__actions');
        footer?.prepend(el);
      }
      el.textContent = message;
      el.hidden = false;
      el.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    }

    _focusMissingBlock(block) {
      const blockEl = this.querySelector(
        `[data-charm-builder-block][data-block-id="${block.id}"]`
      );
      if (!blockEl) return;
      const accordion = blockEl.closest('details.accordion');
      if (accordion && !accordion.open) {
        accordion.open = true;
        accordion.setAttribute('aria-expanded', 'true');
      }
      blockEl.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }

    _setAddBundleLoading(isLoading) {
      this._addingBundle = isLoading;
      this._addBundleBtns.forEach((btn) => {
        btn.disabled = isLoading || !this._validateBundle().valid;
        btn.setAttribute('aria-busy', isLoading ? 'true' : 'false');
      });
    }

    async _addBundleToCart() {
      if (this._addingBundle) return;

      this._clearBundleError();
      const validation = this._validateBundle();

      if (!validation.valid) {
        const message =
          this._data?.labels?.bundleIncomplete ||
          'Please add at least one item to your bundle.';
        this._showBundleError(message);
        return;
      }

      const items = this._buildCartItems();
      if (!items.length) return;

      this._setAddBundleLoading(true);
      document.documentElement.dispatchEvent(
        new CustomEvent('theme:loading:start', { bubbles: true })
      );

      try {
        const sectionsToBundle = [];
        document.documentElement.dispatchEvent(
          new CustomEvent('cart:prepare-bundled-sections', {
            bubbles: true,
            detail: { sections: sectionsToBundle },
          })
        );

        const payload = { items };
        if (sectionsToBundle.length) {
          payload.sections = sectionsToBundle.join(',');
          payload.sections_url = `${window.Shopify?.routes?.root || '/'}variants/${items[0].id}`;
        }

        const response = await fetch(cartRoute('cart/add.js'), {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'X-Requested-With': 'XMLHttpRequest',
          },
          body: JSON.stringify(payload),
        });

        const responseJson = await response.json();

        if (!response.ok) {
          const errorMessage =
            responseJson?.description ||
            this._data?.labels?.bundleAddError ||
            'Could not add bundle to bag. Please try again.';
          this._showBundleError(errorMessage);
          document.documentElement.dispatchEvent(
            new CustomEvent('cart:error', {
              bubbles: true,
              detail: { error: errorMessage },
            })
          );
          return;
        }

        const cartContent = await (await fetch(cartRoute('cart.js'))).json();
        if (responseJson.sections) {
          cartContent.sections = responseJson.sections;
        }

        document.documentElement.dispatchEvent(
          new CustomEvent('cart:change', {
            bubbles: true,
            detail: {
              baseEvent: 'variant:add',
              cart: cartContent,
            },
          })
        );

        this._store.clear();
        this._renderReview(this._store.getState());

        const cartType = window.themeVariables?.settings?.cartType;
        if (cartType === 'page') {
          window.location.href = cartRoute('cart');
        }
      } catch (error) {
        console.error('[CharmBuilder] Add bundle failed', error);
        this._showBundleError(
          this._data?.labels?.bundleAddError ||
            'Could not add bundle to bag. Please try again.'
        );
      } finally {
        document.documentElement.dispatchEvent(
          new CustomEvent('theme:loading:end', { bubbles: true })
        );
        this._setAddBundleLoading(false);
      }
    }

    _createLineItem(line, listType) {
      const isSticky = listType === 'sticky';
      const li = document.createElement('li');
      li.className = isSticky ? 'charm-builder-sticky__item' : 'charm-builder-review__item';
      li.dataset.lineId = line.lineId;

      const img = document.createElement('img');
      img.src = line.image || '';
      img.alt = line.title;
      img.loading = 'lazy';

      const btn = document.createElement('button');
      btn.type = 'button';
      btn.className = isSticky ? 'charm-builder-sticky__remove' : 'charm-builder-review__remove';
      btn.setAttribute('aria-label', 'Remove');
      btn.textContent = '×';

      const handleRemove = (e) => {
        e.stopPropagation();
        this._store.removeLine(line.lineId);
      };

      // On mobile, remove on click anywhere in the li element
      if (MOBILE_MQ.matches) {
        li.addEventListener('click', handleRemove);
      }

      // Always remove on click to the button
      btn.addEventListener('click', handleRemove);

      li.append(img, btn);
      return li;
    }

    _updateStickyBar(state) {
      if (!this._stickyEl) return;

      const modalOpen = this.classList.contains('is-modal-open');
      const hasItems = state.count > 0 && !modalOpen;
      this._stickyEl.toggleAttribute('hidden', !hasItems);
      this._stickyEl.setAttribute('aria-hidden', hasItems ? 'false' : 'true');

      if (this._stickyList) {
        this._stickyList.innerHTML = '';
        state.lines.forEach((line) => {
          this._stickyList.appendChild(this._createLineItem(line, 'sticky'));
        });
      }

      requestAnimationFrame(() => {
        const h = hasItems ? this._stickyEl.offsetHeight : 0;
        this.style.setProperty('--cb-sticky-height', `${h}px`);
      });
    }

    _syncCardSelection(state) {
      this.querySelectorAll('[data-charm-builder-card]').forEach((card) => {
        const blockId = card.dataset.blockId;
        const productId = Number(card.dataset.productId);
        const selected =
          card.dataset.blockType === 'charms'
            ? this._store.hasProduct(blockId, productId)
            : state.selectedProductIds.has(productId) &&
              this._store.hasProduct(blockId, productId);
        card.classList.toggle('is-selected', selected);
        const input = card.querySelector('[data-charm-builder-card-input]');
        if (input) {
          input.checked = selected;
          // Selection is otherwise conveyed only by the card's visual
          // (color/border) state, which screen reader users can't perceive.
          // Fold the status into the accessible name instead.
          const baseLabel = input.dataset.charmBuilderCardLabel || input.getAttribute('aria-label') || '';
          input.setAttribute('aria-label', selected ? `${baseLabel}, Selected` : baseLabel);
        }
      });
    }

    _renderReview(state) {
      const validation = this._validateBundle();

      if (this._reviewList) {
        this._reviewList.innerHTML = '';
        state.lines.forEach((line) => {
          this._reviewList.appendChild(this._createLineItem(line, 'review'));
        });
      }

      if (this._reviewEmpty) {
        this._reviewEmpty.hidden = state.count > 0;
      }

      this._totalEls.forEach((el) => {
        el.textContent = formatMoney(state.total, this._moneyFormat);
      });

      const countsByBlock = new Map();
      state.lines.forEach((line) => {
        countsByBlock.set(line.blockId, (countsByBlock.get(line.blockId) || 0) + 1);
      });
      this._blockCountEls.forEach((el) => {
        el.textContent = countsByBlock.get(el.dataset.blockId) || 0;
      });
      if (this._reviewCountEl) {
        this._reviewCountEl.textContent = state.count;
      }

      this._addBundleBtns.forEach((btn) => {
        btn.disabled = this._addingBundle || !validation.valid;
      });

      this._updateStickyBar(state);
    }
  }

  if (!customElements.get('charm-builder-root')) {
    customElements.define('charm-builder-root', CharmBuilderRoot);
  }
  if (!customElements.get('charm-builder-accordion-group')) {
    customElements.define('charm-builder-accordion-group', CharmBuilderAccordionGroup);
  }

  window.CharmBundleStore = CharmBundleStore;
})();
